import { Link } from 'react-router-dom';
import { ArrowRight, Moon, Gift, Users, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/products/ProductCard';
import { eidFitrProducts } from '../data/products';
import { SEOHead } from '../components/SEOHead';
import { Breadcrumbs } from '../components/Breadcrumbs';

export default function EidFitrPage() {
  return (
    <main className="pt-20 md:pt-24 bg-[#FDFCF8]" data-testid="eid-fitr-page">
      <SEOHead
        title="Eid ul Fitr Geschenke 2026 – Die besten Geschenkideen zum Zuckerfest"
        description="Entdecke die schönsten Eid ul Fitr Geschenke 2026. Handverlesene Geschenkideen für Familie, Kinder und Freunde zum Zuckerfest nach dem Ramadan."
        canonical="/eid-ul-fitr"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 bg-gradient-to-br from-[#4A5D4F] to-[#3A493E] overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-64 h-64 bg-[#D4C5A9]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
                <Moon className="w-4 h-4 text-[#D4C5A9]" />
                <span className="text-sm font-medium text-white/90">
                  Eid Mubarak
                </span>
              </div>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight">
                Eid ul Fitr Geschenke – Freude schenken nach dem Fasten
              </h1>
              <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-8 max-w-lg">
                Eid ul Fitr markiert das Ende des Fastenmonats Ramadan und steht für Dankbarkeit und Gemeinschaft. 
                Hier findest du sorgfältig ausgewählte Eid Geschenke, die Wertschätzung ausdrücken und besondere 
                Momente schaffen. Perfekt für Familien, Kinder und Freunde.
              </p>
              <Button
                asChild
                className="bg-white text-[#4A5D4F] hover:bg-[#F5F5F0] rounded-full px-8 py-6 text-base font-medium"
              >
                <Link to="/geschenke" data-testid="eid-fitr-cta">
                  Eid ul Fitr Geschenke entdecken
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
            </div>
            <div className="hidden lg:block">
              <div className="relative">
                <div className="w-80 h-80 mx-auto bg-[#D4C5A9]/20 rounded-full flex items-center justify-center">
                  <div className="w-60 h-60 bg-[#D4C5A9]/30 rounded-full flex items-center justify-center">
                    <Moon className="w-24 h-24 text-white/80" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Eid ul Fitr */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
            <div className="lg:col-span-2">
              <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
                Über das Fest
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2C332E] mb-6">
                Was ist Eid ul Fitr?
              </h2>
              <div className="prose prose-lg text-[#6B7280] space-y-4">
                <p>
                  Eid ul Fitr, auch bekannt als das „Fest des Fastenbrechens", markiert 
                  das Ende des heiligen Fastenmonats Ramadan. Es ist einer der wichtigsten 
                  Feiertage im islamischen Kalender und wird von Muslimen weltweit gefeiert.
                </p>
                <p>
                  An diesem besonderen Tag kommen Familien zusammen, um gemeinsam zu beten, 
                  festliche Mahlzeiten zu genießen und die Freude des Festes zu teilen. 
                  Es ist Tradition, sich gegenseitig Geschenke zu machen und Bedürftigen 
                  zu helfen (Zakat al-Fitr).
                </p>
                <p>
                  Eid ul Fitr ist eine Zeit der Dankbarkeit, der Vergebung und der 
                  Gemeinschaft – eine Gelegenheit, die Bindungen zu Familie und Freunden 
                  zu stärken und die spirituellen Errungenschaften des Ramadan zu würdigen.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              {[
                { icon: Gift, title: 'Geschenke', text: 'Tradition des Schenkens' },
                { icon: Users, title: 'Familie', text: 'Zeit für Zusammensein' },
                { icon: Heart, title: 'Dankbarkeit', text: 'Freude und Wertschätzung' },
              ].map((item, index) => (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-[#F5F5F0] hover:bg-white hover:shadow-lg transition-all duration-300"
                >
                  <item.icon className="w-8 h-8 text-[#4A5D4F] mb-4" />
                  <h3 className="font-serif text-lg font-medium text-[#2C332E] mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-[#6B7280]">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gift Recommendations */}
      <section className="py-16 md:py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-[#4A5D4F] mb-4 block">
              Empfehlungen
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-medium text-[#2C332E] mb-4">
              Geschenke für Eid ul Fitr
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              Liebevoll ausgewählte Geschenkideen, die perfekt zum Fest des 
              Fastenbrechens passen.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {eidFitrProducts.map((product, index) => (
              <ProductCard key={product.id} product={product} index={index} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Button
              asChild
              variant="outline"
              className="border-[#4A5D4F] text-[#4A5D4F] hover:bg-[#4A5D4F] hover:text-white rounded-full px-8 py-6"
            >
              <Link to="/geschenke" data-testid="view-all-gifts-fitr">
                Alle Geschenke ansehen
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-16 md:py-24 bg-[#2C332E]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white/90 italic leading-relaxed mb-6">
            &ldquo;Eid ist nicht nur ein Fest – es ist eine Gelegenheit, Liebe zu 
            zeigen und Freude zu teilen.&rdquo;
          </p>
          <p className="text-[#D4C5A9] font-medium">
            Eid Mubarak – Möge euer Fest gesegnet sein
          </p>
        </div>
      </section>
    </main>
  );
}
